import React, { useEffect, useState, useRef } from 'react';
import { Unity, useUnityContext } from "react-unity-webgl";
import { useTranslation } from 'react-i18next';
import styles from '../../static/styles/AimTrain.module.css';
import CannonClashPopup from '../../components/CannonClashPopup';
import loadingImg from '../../static/img/MadeByESL/coverCannonClash.jpeg';
import { useNavigate, useLocation } from 'react-router-dom';
import { eslTourMatchResult } from '../../api/tournaments';

const CannonClash2D = () => {
  const { t } = useTranslation();
  const [contextError, setContextError] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [gameStatus, setGameStatus] = useState('waiting');
  const gameContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    unityProvider,
    isLoaded,
    sendMessage,
    addEventListener,
    removeEventListener,
    unload,
    loadingProgression
  } = useUnityContext({
    loaderUrl: "CannonClash2D/Build/CannonClash2D.loader.js",
    dataUrl: "CannonClash2D/Build/CannonClash2D.data.unityweb",
    frameworkUrl: "CannonClash2D/Build/CannonClash2D.framework.js.unityweb",
    codeUrl: "CannonClash2D/Build/CannonClash2D.wasm.unityweb",
    webglContextAttributes: {
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
      failIfMajorPerformanceCaveat: false,
      majorVersion: 1,
      minorVersion: 0
    }
  });

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Send room data to Unity when loaded
  useEffect(() => {
    if (isLoaded) {
      setTimeout(() => {
        try {
          const msisdn = localStorage.getItem('msisdn') || `player_${Date.now()}`;
          const roomName = location.state?.roomId || "test room";

          //console.log(`Sending to Unity - Room: ${roomName}, Player: ${msisdn}`);

          sendMessage(
            "LobbyManager",
            "LoadRoomDataFromReact",
            `${roomName}|${msisdn}`
          );
        } catch (e) {
          //console.error("Failed to send room data to Unity:", e);
        }
      }, 1000);
    }
  }, [isLoaded, sendMessage, location.state]);

  // Handle Unity callbacks
  useEffect(() => {
    function handleWaitingForOpponent() {
      setGameStatus('waiting');
      setPopupMessage(t('loading'));
      setPopupOpen(true);
    }

    function handleGameStart() {
      setGameStatus('playing');
      setPopupMessage(t('play_now'));
      setPopupOpen(true);
    }

    function handleGameEnd(result) {
      const [outcome, playerId] = result.split('|');
      setGameStatus('finished');
      //console.log(`Game ended: ${outcome} for player ${playerId}`);

      if (location.state?.tournamentId && location.state?.roomId) {
        const resultFlag = outcome === 'victory' ? 'winner' : 'loser';
        eslTourMatchResult(location.state.tournamentId, location.state.roomId, resultFlag)
          .then(response => {
            //console.log('Tournament result submitted:', response);
          })
          .catch(error => {
            //console.error('Failed to submit tournament result:', error);
          });
      }

      if (outcome === 'victory') {
        setPopupMessage(t('success_join'));
      } else {
        setPopupMessage(t('err_long'));
      }
      setPopupOpen(true);
    }

    function handleConnectionError(errorMessage) {
      setPopupMessage(`${t('err')}: ${errorMessage}`);
      setPopupOpen(true);
    }

    window.onWaitingForOpponent = handleWaitingForOpponent;
    window.onGameStart = handleGameStart;
    window.onGameEnd = handleGameEnd;
    window.onConnectionError = handleConnectionError;

    return () => {
      if (window.onWaitingForOpponent === handleWaitingForOpponent) window.onWaitingForOpponent = undefined;
      if (window.onGameStart === handleGameStart) window.onGameStart = undefined;
      if (window.onGameEnd === handleGameEnd) window.onGameEnd = undefined;
      if (window.onConnectionError === handleConnectionError) window.onConnectionError = undefined;
    };
  }, [t, location.state]);

  // Error handling
  useEffect(() => {
    const handleError = () => {
      setContextError(true);
    };

    addEventListener("error", handleError);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && contextError) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      removeEventListener("error", handleError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unload();
    };
  }, [addEventListener, removeEventListener, unload, contextError]);



  const handlePopupClose = () => {
    setPopupOpen(false);
    if (gameStatus === 'finished') {
      unload();
      navigate('/tournaments');
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  useEffect(() => {
    document.title = `Cannon Clash | Jazz ESL`;
  }, []);

  useEffect(() => {
    if (!location.state?.roomId) {
      navigate('/tournaments');
    }
  }, [location.state, navigate]);

  const TOTAL_MB = 22.5;
  const loadedMB = Math.max(0, Math.min(TOTAL_MB, Math.round((loadingProgression || 0) * TOTAL_MB * 10) / 10));

  return (
    <div
      ref={gameContainerRef}
      className={styles.gameContainer}
    >
      {!contextError && (
        <div
          className={styles.unityWrapper}
          style={{
            visibility: isLoaded ? 'visible' : 'hidden'
          }}
        >
          <Unity
            unityProvider={unityProvider}
            style={{ width: '100%', height: '100%' }}
            devicePixelRatio={window.devicePixelRatio}
          />
        </div>
      )}

      {contextError && (
        <div className={styles.errorOverlay}>
          <h2 className={styles.errorTitle}>{t('err')}</h2>
          <p className={styles.errorMessage}>
            {t('smth_wrong')}
          </p>
          <button className={styles.reloadButton} onClick={handleReload}>
            {t('loading')}
          </button>
        </div>
      )}

      {!isLoaded && !contextError && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingBox}>
            <img
              src={loadingImg}
              alt="Loading Cannon Clash 2D"
              className={styles.loadingImgBg}
              draggable={false}
            />
            <div className={styles.loadingProgressBarContainer}>
              <div
                className={styles.loadingProgressBar}
                style={{ width: `${Math.round((loadingProgression || 0) * 100)}%` }}
              />
            </div>
            <div className={styles.loadingText}>
              {t('loading')} {Math.round((loadingProgression || 0) * 100)}%
            </div>
            <div className={styles.loadingStats}>
              {loadedMB} MB / {TOTAL_MB} MB
            </div>
          </div>
        </div>
      )}

      <CannonClashPopup
        isOpen={popupOpen}
        message={popupMessage}
        onClose={handlePopupClose}
      />
    </div>
  );
};

export default CannonClash2D;